import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { stopSpeaking } from '@/lib/speech';
import {
  AUTO_APPLY_ACTIONS,
  QUICK_ACTIONS,
  type AnyEditor,
  type ChatMessage,
  type ChatMode,
  type DocEdit,
  type ToolCall,
} from '@/components/editor/ai/aiChatShared';
import {
  buildInvestmentContent,
  buildPageContent,
  chatStorageKey,
  clearAllAiEditing,
  clearBlockAiEditing,
  closeOpenTags,
  getBlockIdForPage,
  setBlockAiEditing,
} from '@/components/editor/ai/aiChatEditorUtils';

interface UseAIChatStreamParams {
  quoteId?: string;
  mode: ChatMode;
  resolveEditor: () => AnyEditor | null;
  getSelectedText: () => string;
  getDocumentContext: () => string;
  onCreateBlock?: (
    type: string,
    content: Record<string, unknown>,
    insertAfterPage?: number,
  ) => Promise<{ blockId: string } | null>;
  onDeleteBlock?: (pageNumber: number) => Promise<boolean>;
  onMoveBlock?: (pageNumber: number, newPosition: number) => Promise<boolean>;
  onSyncEditor?: () => void;
  stopSpeechInput: () => void;
  /** Wordt aangeroepen zodra het bericht in de lijst staat (origineel: setInput('')). */
  onSendStart?: () => void;
}

/**
 * SSE-streaming en tool-uitvoering van de AI-chat, losgekoppeld van de UI.
 * Beheert berichten (incl. localStorage-persistentie per offerte), doc-edits
 * (skipHistory/addToHistory: false tijdens streamen + onSyncEditor-flush na afloop)
 * en de create/delete/move tool-calls.
 */
export function useAIChatStream({
  quoteId,
  mode,
  resolveEditor,
  getSelectedText,
  getDocumentContext,
  onCreateBlock,
  onDeleteBlock,
  onMoveBlock,
  onSyncEditor,
  stopSpeechInput,
  onSendStart,
}: UseAIChatStreamParams) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [streamingPreview, setStreamingPreview] = useState('');
  const [toolResults, setToolResults] = useState<Record<string, { blockId: string } | null>>({});

  const abortControllerRef = useRef<AbortController | null>(null);
  const editBufferRef = useRef<string>('');
  const applyTimerRef = useRef<number | null>(null);
  const currentEditPageRef = useRef<number>(0);

  useEffect(() => {
    if (!quoteId) return;
    try {
      const stored = localStorage.getItem(chatStorageKey(quoteId));
      if (stored) setMessages(JSON.parse(stored));
    } catch {
      /* ignore corrupt data */
    }
  }, [quoteId]);

  useEffect(() => {
    if (!quoteId || isStreaming) return;
    if (messages.length === 0) {
      localStorage.removeItem(chatStorageKey(quoteId));
    } else {
      localStorage.setItem(chatStorageKey(quoteId), JSON.stringify(messages));
    }
  }, [messages, quoteId, isStreaming]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const executeToolCall = useCallback(
    async (toolCall: ToolCall): Promise<{ blockId: string } | null> => {
      if (!onCreateBlock) return null;

      if (toolCall.name === 'create_page') {
        const content = buildPageContent(toolCall.input);
        const type = (toolCall.input.page_type as string) || 'CONTENT_PAGE';
        const afterPage = toolCall.input.insert_after_page as number | undefined;
        return await onCreateBlock(type, content, afterPage);
      }

      if (toolCall.name === 'create_investment_table') {
        const content = buildInvestmentContent(toolCall.input);
        const afterPage = toolCall.input.insert_after_page as number | undefined;
        return await onCreateBlock('INVESTMENT', content, afterPage);
      }

      return null;
    },
    [onCreateBlock],
  );

  const applyEditToPage = useCallback(
    (pageNumber: number, rawHtml: string, editMode: string, skipHistory = false) => {
      const ed = resolveEditor();
      if (!ed) return;

      let html = rawHtml
        .replace(/\\(?![nrt"\\/<>])/g, '')
        .replace(/\\n/g, '')
        .replace(/\\t/g, '')
        .replace(/\\"/g, '"')
        .replace(/\\\\/g, '\\')
        .replace(/>\s+</g, '><')
        .replace(/\n+/g, '')
        .trim();

      if (html && !html.startsWith('<')) {
        html = `<p>${html}</p>`;
      }

      if (!html) return;

      if (skipHistory) {
        html = closeOpenTags(html);
        if (!html) return;
      }

      let currentIndex = 0;
      let targetPos: number | null = null;
      let targetNodeSize: number | null = null;
      ed.state.doc.forEach((node: any, offset: number) => {
        currentIndex++;
        if (currentIndex === pageNumber && targetPos === null) {
          targetPos = offset;
          targetNodeSize = node.nodeSize;
        }
      });
      if (targetPos === null || targetNodeSize === null) return;

      if (editMode === 'append') {
        const insertAt = targetPos + targetNodeSize - 1;
        const chain = ed.chain();
        if (skipHistory) chain.command(({ tr }: { tr: any }) => { tr.setMeta('addToHistory', false); return true; });
        else chain.focus();
        chain.insertContentAt(insertAt, html).run();
      } else {
        const innerStart = targetPos + 1;
        const innerEnd = targetPos + targetNodeSize - 1;
        const chain = ed.chain();
        if (skipHistory) chain.command(({ tr }: { tr: any }) => { tr.setMeta('addToHistory', false); return true; });
        else chain.focus();
        chain
          .command(({ tr }: { tr: any }) => {
            tr.delete(innerStart, innerEnd);
            return true;
          })
          .insertContentAt(innerStart, html)
          .run();
      }
    },
    [resolveEditor],
  );

  const addDocEdit = useCallback(
    (msgId: string, edit: DocEdit) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msgId
            ? { ...m, docEdits: [...(m.docEdits || []), edit] }
            : m,
        ),
      );
    },
    [],
  );

  const updateDocEditStatus = useCallback(
    (msgId: string, page: number, status: DocEdit['status']) => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === msgId
            ? {
                ...m,
                docEdits: (m.docEdits || []).map((de) =>
                  de.page === page ? { ...de, status } : de,
                ),
              }
            : m,
        ),
      );
    },
    [],
  );

  const sendMessage = useCallback(
    async (text: string, action?: string) => {
      stopSpeaking();
      stopSpeechInput();
      const ed = resolveEditor();
      const sel = getSelectedText();
      const documentContext = getDocumentContext();
      const actionLabel = QUICK_ACTIONS.find((a) => a.id === action)?.label || action;
      const shouldAutoApply = mode === 'ask' && action && AUTO_APPLY_ACTIONS.has(action) && sel && ed;

      let selFrom: number | undefined;
      let selTo: number | undefined;
      if (shouldAutoApply && ed) {
        selFrom = ed.state.selection.from;
        selTo = ed.state.selection.to;
      }

      let userContent: string;
      if (action && sel) {
        userContent = `${actionLabel}: "${sel.slice(0, 200)}${sel.length > 200 ? '...' : ''}"`;
      } else if (action && !sel) {
        userContent = `${actionLabel} het hele document`;
      } else {
        userContent = text;
      }

      const userMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'user',
        content: userContent,
        action,
        selectedText: sel || undefined,
        hadSelection: !!sel,
      };

      const assistantMsg: ChatMessage = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: '',
        action,
        hadSelection: !!sel,
        selectionFrom: selFrom,
        selectionTo: selTo,
      };

      setMessages((prev) => [...prev, userMsg, assistantMsg]);
      onSendStart?.();
      setIsStreaming(true);

      const controller = new AbortController();
      abortControllerRef.current = controller;
      let fullContent = '';

      try {
        const chatHistory = [...messages, userMsg].map((m) => ({
          role: m.role,
          content: m.content,
        }));

        const stream = api.streamChat(
          chatHistory,
          sel || undefined,
          action,
          documentContext || undefined,
          controller.signal,
          mode,
        );

        let receivedContent = false;
        let hasError = false;
        let currentEditPage = 0;
        let currentEditMode = 'replace';

        for await (const event of stream) {
          if (controller.signal.aborted) break;

          if (event.type === 'thinking' && event.content) {
            receivedContent = true;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsg.id
                  ? { ...m, thinking: (m.thinking || '') + event.content }
                  : m,
              ),
            );
          } else if (event.type === 'text' && event.content) {
            receivedContent = true;
            fullContent += event.content;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsg.id
                  ? { ...m, content: m.content + event.content }
                  : m,
              ),
            );
          } else if (event.type === 'doc_edit_start') {
            receivedContent = true;
            currentEditPage = (event as any).page || 0;
            currentEditMode = (event as any).editMode || 'replace';
            currentEditPageRef.current = currentEditPage;
            editBufferRef.current = '';
            setStreamingPreview('');
            addDocEdit(assistantMsg.id, {
              type: 'edit',
              page: currentEditPage,
              status: 'in_progress',
              editMode: currentEditMode,
            });
            const edRef = resolveEditor();
            if (edRef && currentEditPage > 0) {
              const blockId = getBlockIdForPage(edRef, currentEditPage);
              if (blockId) setBlockAiEditing(blockId);
            }
          } else if (event.type === 'doc_edit_delta' && event.content) {
            editBufferRef.current += event.content;

            if (!applyTimerRef.current && currentEditPage > 0) {
              applyTimerRef.current = window.setTimeout(() => {
                applyTimerRef.current = null;
                if (editBufferRef.current) {
                  applyEditToPage(currentEditPage, editBufferRef.current, currentEditMode, true);
                  setStreamingPreview(closeOpenTags(editBufferRef.current));
                }
              }, 150);
            }
          } else if (event.type === 'doc_edit_end') {
            if (applyTimerRef.current) {
              clearTimeout(applyTimerRef.current);
              applyTimerRef.current = null;
            }

            if (currentEditPage > 0 && editBufferRef.current) {
              applyEditToPage(currentEditPage, editBufferRef.current, currentEditMode);
              onSyncEditor?.();
            }
            const edRef = resolveEditor();
            if (edRef && currentEditPage > 0) {
              const blockId = getBlockIdForPage(edRef, currentEditPage);
              if (blockId) clearBlockAiEditing(blockId);
            }
            updateDocEditStatus(assistantMsg.id, currentEditPage, 'done');
            setStreamingPreview('');
            currentEditPageRef.current = 0;
            editBufferRef.current = '';
            currentEditPage = 0;
          } else if (event.type === 'doc_action') {
            receivedContent = true;
            const actionType = (event as any).action as string;
            const page = (event as any).page as number;

            if (actionType === 'delete' && onDeleteBlock) {
              addDocEdit(assistantMsg.id, { type: 'delete', page, status: 'in_progress' });
              const ok = await onDeleteBlock(page);
              updateDocEditStatus(assistantMsg.id, page, ok ? 'done' : 'error');
            } else if (actionType === 'move' && onMoveBlock) {
              const newPos = (event as any).newPosition as number;
              addDocEdit(assistantMsg.id, { type: 'move', page, status: 'in_progress', newPosition: newPos });
              const ok = await onMoveBlock(page, newPos);
              updateDocEditStatus(assistantMsg.id, page, ok ? 'done' : 'error');
            }
          } else if (event.type === 'tool_use' && event.name) {
            receivedContent = true;
            const tc: ToolCall = {
              name: event.name!,
              input: (event.input || {}) as Record<string, any>,
            };
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsg.id
                  ? { ...m, toolCalls: [...(m.toolCalls || []), tc] }
                  : m,
              ),
            );
            if (tc.name === 'create_page' || tc.name === 'create_investment_table') {
              const tcKey = `${assistantMsg.id}-${tc.name}-${Date.now()}`;
              try {
                const result = await executeToolCall(tc);
                setToolResults((prev) => ({ ...prev, [tcKey]: result }));
                setMessages((prev) =>
                  prev.map((m) =>
                    m.id === assistantMsg.id
                      ? {
                          ...m,
                          toolCalls: (m.toolCalls || []).map((existing) =>
                            existing === tc ? { ...existing, _resultKey: tcKey } : existing,
                          ),
                        }
                      : m,
                  ),
                );
              } catch {
                setToolResults((prev) => ({ ...prev, [tcKey]: null }));
              }
            }
          } else if (event.type === 'error') {
            hasError = true;
            setMessages((prev) =>
              prev.map((m) =>
                m.id === assistantMsg.id
                  ? { ...m, content: `Fout: ${event.content || 'Onbekende fout'}` }
                  : m,
              ),
            );
            fullContent = '';
            break;
          } else if (event.type === 'done') {
            break;
          }
        }

        if (!receivedContent && !hasError && !controller.signal.aborted) {
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMsg.id
                ? { ...m, content: 'Er ging iets mis bij het ophalen van het antwoord. Probeer het opnieuw.' }
                : m,
            ),
          );
          fullContent = '';
        }
      } catch (err: unknown) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          /* intentional abort */
        } else {
          const errorMessage = err instanceof Error ? err.message : 'Onbekende fout';
          setMessages((prev) =>
            prev.map((m) =>
              m.id === assistantMsg.id
                ? { ...m, content: `Fout: ${errorMessage}` }
                : m,
            ),
          );
          fullContent = '';
        }
      } finally {
        if (applyTimerRef.current) {
          clearTimeout(applyTimerRef.current);
          applyTimerRef.current = null;
        }
        clearAllAiEditing();
        setStreamingPreview('');
        currentEditPageRef.current = 0;
        setIsStreaming(false);
        abortControllerRef.current = null;
      }

      if (shouldAutoApply && fullContent && selFrom !== undefined && selTo !== undefined && ed) {
        ed.chain()
          .focus()
          .deleteRange({ from: selFrom, to: selTo })
          .insertContentAt(selFrom, fullContent)
          .run();
        setMessages((prev) =>
          prev.map((m) => (m.id === assistantMsg.id ? { ...m, applied: true } : m)),
        );
      }
    },
    [messages, getSelectedText, getDocumentContext, resolveEditor, executeToolCall, mode, addDocEdit, updateDocEditStatus, applyEditToPage, onDeleteBlock, onMoveBlock, onSyncEditor, stopSpeechInput, onSendStart],
  );

  const stopStreaming = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    setIsStreaming(false);
    stopSpeaking();
    stopSpeechInput();
  }, [stopSpeechInput]);

  return {
    messages,
    setMessages,
    isStreaming,
    streamingPreview,
    toolResults,
    currentEditPageRef,
    sendMessage,
    stopStreaming,
  };
}
